import React, { useEffect, useState } from 'react';

import { useMsal } from '@azure/msal-react';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import SaveIcon from '@mui/icons-material/Save';
import ShareIcon from '@mui/icons-material/Share';
import TableChart from '@mui/icons-material/TableChart';
import { colors, Stack } from '@mui/material';
import { useAtom } from 'jotai';

import useCreateEventException from '../../../../hooks/useCreateEventException/useCreateEventException';
import useDeleteEvent from '../../../../hooks/useDeleteEvent/useDeleteEvent';
import {
  eventPaneLoadingAtom,
  formModeAtom,
  isSeriesEventActionAtom,
  notificationMessageAtom,
  selectedEventAtom,
  showEventPaneAtom,
} from '../../../../state/atoms/atoms';
import DeleteConfirmationDialog from '../DeleteConfirmationDialog/DeleteConfirmationDialog';

import { Button } from './Button/Button';
import { Popover } from './Popover/Popover';

export const EventPaneActionBar = ({ allowEdit }: { allowEdit?: boolean }) => {
  const { instance } = useMsal();
  const bypassRoleForE2E = import.meta.env.VITE_E2E === 'true';
  const userRoles = instance?.getAllAccounts()?.[0]?.idTokenClaims?.roles;
  const currentUserMpId = instance?.getAllAccounts()?.[0]?.idTokenClaims?.onpremisesuserprincipalname as string;
  const [formMode, setFormMode] = useAtom(formModeAtom);
  const [selectedEvent, setSelectedEvent] = useAtom(selectedEventAtom);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [, setShowEventPane] = useAtom(showEventPaneAtom);
  const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
  const [isSeriesEventAction, setIsSeriesEventAction] = useAtom(isSeriesEventActionAtom);
  const [loading] = useAtom(eventPaneLoadingAtom);
  const [isCopied, setIsCopied] = useState(false);
  const [, setNotificationMessage] = useAtom(notificationMessageAtom);
  const { deleteEvent } = useDeleteEvent();
  const { createEventException } = useCreateEventException();
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (isCopied) {
      timer = setTimeout(() => {
        setIsCopied(false);
      }, 3500);
    }
    return () => clearTimeout(timer);
  }, [isCopied]);

  const canEdit = () => {
    if (allowEdit) return true;
    if (bypassRoleForE2E && formMode === 'preview') {
      return true;
    }
    if (userRoles?.includes('GEC.Admin') && formMode === 'preview') {
      return true;
    }
    if (formMode === 'preview' && selectedEvent?.created_by === currentUserMpId) {
      return true;
    }
    return false;
  };
  const canDelete = () => {
    if (bypassRoleForE2E && formMode !== 'new') {
      return true;
    }
    if (userRoles?.includes('GEC.Admin') && formMode !== 'new') {
      return true;
    }
    if (formMode !== 'new' && selectedEvent?.created_by === currentUserMpId) {
      return true;
    }
    return false;
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleCloseSharePopover = () => {
    setNotificationMessage({
      messageTitle: 'Event Link Copied',
      messageBody: 'The event link has been copied to your clipboard.',
      intent: 'success',
    });
  };

  const isEventAlreadyException = () => {
    if (!selectedEvent?.isRecurring || !selectedEvent?.eventDateTime?.start || !selectedEvent?.recurrence?.exceptions) {
      return false;
    }
    const currentEventDate = selectedEvent.eventDateTime.start.format('YYYY-MM-DD');
    return selectedEvent.recurrence.exceptions.some(
      (exception) => exception.exceptionDate.format('YYYY-MM-DD') === currentEventDate,
    );
  };

  // Define button configurations and map over each
  const buttonConfigs = [
    {
      key: 'edit-event',
      title: 'Edit Event',
      ariaDescribedBy: undefined,
      size: 'small' as const,
      variant: 'text' as const,
      color: 'inherit' as const,
      text: 'Edit Event' as const,
      startIcon: <EditIcon fontSize="small" />,
      onClick: () => {
        setIsSeriesEventAction(false);
        setFormMode('edit');
      },
      show: canEdit(),
      disabled: false,
      popover: undefined,
    },
    {
      key: 'edit-series',
      title: 'Edit Series',
      ariaDescribedBy: undefined,
      size: 'small' as const,
      variant: 'text' as const,
      color: 'inherit' as const,
      text: 'Edit Series' as const,
      startIcon: <TableChart fontSize="small" />,
      onClick: () => {
        setIsSeriesEventAction(true);
        setFormMode('edit');
      },
      show: canEdit() && selectedEvent?.isRecurring,
      disabled: false,
      popover: null,
    },
    {
      key: 'save-event',
      title: 'Save Event',
      ariaDescribedBy: undefined,
      size: 'small' as const,
      variant: 'text' as const,
      color: 'inherit' as const,
      text: 'Save' as const,
      startIcon: <SaveIcon fontSize="small" />,
      onClick: undefined,
      show: formMode !== 'preview',
      disabled: loading,
      popover: undefined,
      type: 'submit' as const,
      form: 'event-form' as const,
    },
    {
      key: 'delete-event',
      title: 'Delete Single Event',
      ariaDescribedBy: undefined,
      size: 'small' as const,
      variant: 'text' as const,
      color: 'inherit' as const,
      text: 'Delete Event' as const,
      startIcon: <DeleteIcon fontSize="small" />,
      onClick: () => {
        setIsSeriesEventAction(false);
        setShowDeleteConfirmationDialog(true);
      },
      show: canDelete(),
      disabled: isEventAlreadyException(),
      popover: null,
    },
    {
      key: 'delete-series',
      title: 'Delete Recurrence Series',
      ariaDescribedBy: undefined,
      size: 'small' as const,
      variant: 'text' as const,
      color: 'inherit' as const,
      text: 'Delete Series' as const,
      startIcon: <EventBusyIcon fontSize="small" />,
      onClick: () => {
        setIsSeriesEventAction(true);
        setShowDeleteConfirmationDialog(true);
      },
      show: canDelete() && selectedEvent?.isRecurring,
      disabled: false,
      popover: null,
    },
    {
      key: 'share-event',
      title: 'Share',
      ariaDescribedBy: id,
      size: 'small' as const,
      variant: 'text' as const,
      color: 'inherit' as const,
      text: 'Share' as const,
      startIcon: <ShareIcon fontSize="small" />,
      onClick: (event: React.MouseEvent<HTMLButtonElement>) => handleClick(event),
      show: Boolean(selectedEvent),
      disabled: !selectedEvent?.id || loading,
      popover: (
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          handleClose={handleClose}
          isCopied={isCopied}
          setIsCopied={setIsCopied}
          selectedEvent={selectedEvent}
          handleCloseSharePopover={handleCloseSharePopover}
          setNotificationMessage={setNotificationMessage}
        />
      ),
    },
  ];

  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1,
        backgroundColor: 'primary.main',
        color: colors.common.white,
        flexWrap: 'wrap',
      }}
    >
      {buttonConfigs.map((config) => (
        <Button
          key={config.key}
          title={config.title}
          ariaDescribedBy={config.ariaDescribedBy}
          size={config.size}
          variant={config.variant}
          color={config.color}
          text={config.text}
          startIcon={config.startIcon}
          onClick={config.onClick}
          show={config.show}
          disabled={config.disabled}
          popover={config.popover}
          type={config.type}
          form={config.form}
        />
      ))}

      <DeleteConfirmationDialog
        open={showDeleteConfirmationDialog}
        setOpen={setShowDeleteConfirmationDialog}
        isSeriesEventAction={isSeriesEventAction}
        onDeleteConfirm={function (): void {
          if (!selectedEvent?.id) {
            console.error('No event selected to delete.');
            setNotificationMessage({
              messageTitle: 'No event selected to delete.',
              messageBody: 'Please select an event to delete.',
              intent: 'error',
            });
            return;
          }
          if (isSeriesEventAction) {
            // delete series
            deleteEvent(selectedEvent.id);
            setShowEventPane(false);
            setSelectedEvent(null);
            setNotificationMessage({
              messageTitle: 'Series Deleted',
              messageBody: 'The series has been successfully deleted.',
              intent: 'success',
            });
          } else if (!isSeriesEventAction && selectedEvent.isRecurring) {
            // delete single event from series
            void createEventException(
              selectedEvent.id,
              selectedEvent.eventDateTime?.start?.toDate() as Date,
              currentUserMpId,
            );
            setShowEventPane(false);
            setSelectedEvent(null);
            setNotificationMessage({
              messageTitle: 'Event Deleted',
              messageBody: 'The event has been successfully deleted from the series.',
              intent: 'success',
            });
          } else {
            // delete single event
            deleteEvent(selectedEvent.id);
            setShowEventPane(false);
            setSelectedEvent(null);
            setNotificationMessage({
              messageTitle: 'Event Deleted',
              messageBody: 'The event has been successfully deleted.',
              intent: 'success',
            });
          }
        }}
      />
    </Stack>
  );
};
